import React, { FC } from 'react';
import cn from 'classnames';
import { useLandingPageContext } from 'context/LandingPageContext';
import { Discount } from 'store/landing/types';
import { useTranslation } from 'react-i18next';
import extractTextBetweenBrackets from 'utils/extractTextBetweenBrackets';
import LPButton, { ADD_OP_PLUS_REFERENCE, LPButtonSize } from '../LPButton/LPButton';
import styles from './MediumBanner.module.scss';
import DiscountBubble from '../DiscountBubble/DiscountBubble';

const isDiscountExpired = (expirationDate: Date) => {
  return expirationDate.getTime() < new Date().getTime();
};
export interface MediumBannerProps {
  imageUrl: string;
  imageUrlMobile: string;
  referenceLabel: string;
  referenceUrl: string;
  referenceLabelColor: string;
  referenceBackgroundColor: string;
  referenceBorderColor: string;
  mode?: 'grid' | 'carousel';
  discountBubble?: Discount;
  title?: string;
  subtitle?: string;
}

const MediumBanner: FC<MediumBannerProps> = ({
  imageUrl,
  imageUrlMobile,
  referenceLabel,
  referenceLabelColor,
  referenceBackgroundColor,
  referenceUrl,
  discountBubble,
  title,
  subtitle,
  mode = 'grid',
}) => {
  const { t } = useTranslation();
  const { theme, addOpPlus, isDesktopDevice } = useLandingPageContext();
  const showDiscountBubble =
    discountBubble &&
    discountBubble.label &&
    discountBubble?.voucherExpirationDate &&
    !isDiscountExpired(new Date(discountBubble?.voucherExpirationDate));

  const redirectAfterPlusAdded = referenceUrl.includes(ADD_OP_PLUS_REFERENCE)
    ? extractTextBetweenBrackets(referenceUrl)
    : undefined;

  const openReferenceUrl = () => {
    if (referenceUrl.includes(ADD_OP_PLUS_REFERENCE) || referenceUrl.includes(ADD_OP_PLUS_REFERENCE)) {
      addOpPlus(true, redirectAfterPlusAdded || '');
      return;
    }
    window.location.href = referenceUrl;
  };

  if (theme === 'studio') {
    return (
      <div
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
          openReferenceUrl();
        }}
        className={cn(styles.mediumBanner, styles[`container--${mode}`], styles[`theme-${theme}`])}
      >
        <div className={cn(styles.image, styles[`image--${mode}`])}>
          <div style={{ background: `url(${isDesktopDevice ? imageUrl : imageUrlMobile})` }} />
        </div>
        <div
          className={styles.mediumBannerCaptionsBox}
          style={{
            position: isDesktopDevice || mode === 'grid' ? 'absolute' : 'relative',
          }}
        >
          <LPButton
            buttonColor={referenceLabelColor}
            referenceUrl={referenceUrl}
            size={isDesktopDevice ? LPButtonSize.md : LPButtonSize.xsm}
          >
            {referenceLabel}
          </LPButton>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => {
        openReferenceUrl();
      }}
      className={cn(styles.mediumBanner, styles[`theme-${theme}`], styles[`container-${mode}`])}
    >
      <div className={styles.mediumBannerInner}>
        <div className={cn(styles.image)}>
          <div
            className={styles.imageInner}
            style={{ backgroundImage: `url(${isDesktopDevice ? imageUrl : imageUrlMobile})` }}
          />
          <div className={styles.overlay} />
          {showDiscountBubble && (
            <div className={styles.discountBubble}>
              <DiscountBubble
                bubbleBackgroundColor={discountBubble?.bubbleBackgroundColor}
                bubbleBorderColor={discountBubble?.bubbleBorderColor}
                fontColor={discountBubble?.fontColor}
                label={discountBubble?.label}
              />
            </div>
          )}
        </div>
        <div className={styles.mediumBannerCaptionsBox}>
          {title && <h1 className={styles.title}>{title}</h1>}
          {subtitle && <h2 className={styles.subtitle}>{subtitle}</h2>}
          <LPButton
            buttonColor={referenceLabelColor}
            referenceUrl={referenceUrl}
            size={LPButtonSize.sm}
            buttonBackgroundColor={referenceBackgroundColor}
          >
            {referenceLabel}
          </LPButton>
        </div>
      </div>
    </div>
  );
};

export default MediumBanner;
